import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import ImageExt from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import { toast } from "sonner";
import { ArrowLeft, Save, UploadCloud, Image as ImgIcon, Bold, Italic, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Quote, Undo, Redo, Sparkles, X, Copy, Clipboard } from "lucide-react";
import { api, resolveImageUrl } from "@/lib/api";

const slugify = (t) => t.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s-]+/g, "-").slice(0, 120);

export default function AdminBlogEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState({
    title: "", subtitle: "", slug: "", excerpt: "", category: "Executive Insights", tags: "",
    author: "Aftab Azad", meta_title: "", meta_description: "", status: "draft", hero_image: null,
    destination: "insights", internal_notes: "",
    industry: "", business_situation: "", executive_challenge: "", approach: "",
    strategic_intervention: "", business_outcomes: "", key_takeaways: "", cta: "",
  });
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(isNew);
  const [uploading, setUploading] = useState(false);
  const heroInput = useRef(null);
  const inlineInput = useRef(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiKind, setAiKind] = useState("seo");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      LinkExt.configure({ openOnClick: false, HTMLAttributes: { class: "text-royal underline" } }),
      ImageExt.configure({ HTMLAttributes: { class: "rounded-lg border border-slate-200" } }),
    ],
    content: "",
    editorProps: { attributes: { class: "prose max-w-none min-h-[420px] p-6 focus:outline-none" } },
    onUpdate: ({ editor }) => setContent(editor.getHTML()),
  });

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/blogs/${id}`);
        setForm({
          title: data.title || "", subtitle: data.subtitle || "", slug: data.slug || "", excerpt: data.excerpt || "",
          category: data.category || "Executive Insights", tags: (data.tags || []).join(", "),
          author: data.author || "Aftab Azad", meta_title: data.meta_title || "",
          meta_description: data.meta_description || "", status: data.status || "draft",
          hero_image: data.hero_image || null,
          destination: data.destination || "insights", internal_notes: data.internal_notes || "",
          industry: data.industry || "", business_situation: data.business_situation || "",
          executive_challenge: data.executive_challenge || "", approach: data.approach || "",
          strategic_intervention: data.strategic_intervention || "",
          business_outcomes: data.business_outcomes || "",
          key_takeaways: data.key_takeaways || "", cta: data.cta || "",
        });
        setContent(data.content || "");
        if (editor) editor.commands.setContent(data.content || "");
        setLoaded(true);
      } catch { toast.error("Failed to load article"); }
    })();
  }, [id, editor, isNew]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const upload = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } catch { toast.error("Upload failed"); return null; }
    finally { setUploading(false); }
  };

  const onHero = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const url = await upload(file);
    if (url) { setForm((f) => ({ ...f, hero_image: url })); toast.success("Hero image uploaded"); }
  };

  const onInline = async (e) => {
    const file = e.target.files?.[0]; if (!file || !editor) return;
    const url = await upload(file);
    if (url) editor.chain().focus().setImage({ src: resolveImageUrl(url), alt: form.title || "Article image" }).run();
  };

  const generateAI = async (kind) => {
    if (!form.title.trim() || !content) { toast.error("Add a title and some content first"); return; }
    setAiBusy(true); setAiResult(null); setAiKind(kind);
    try {
      const { data } = await api.post("/admin/ai/generate", { kind, title: form.title, subtitle: form.subtitle, content });
      setAiResult(data.result);
    } catch (err) {
      const msg = err?.response?.data?.detail || "AI generation failed";
      toast.error(typeof msg === "string" ? msg : "AI generation failed");
    } finally { setAiBusy(false); }
  };

  const applyAI = (key, value) => {
    if (!value) return;
    setForm((f) => ({ ...f, [key]: value }));
    toast.success("Applied to editor");
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text || ""); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  const save = async (nextStatus) => {
    if (!form.title.trim()) { toast.error("A title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle || null,
        slug: form.slug ? slugify(form.slug) : slugify(form.title),
        excerpt: form.excerpt || "", content: content || "",
        hero_image: form.hero_image, category: form.category || "Executive Insights",
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        author: form.author, meta_title: form.meta_title || null,
        meta_description: form.meta_description || null, status: nextStatus || form.status,
        destination: form.destination || "insights",
        internal_notes: form.internal_notes || null,
        industry: form.industry || null,
        business_situation: form.business_situation || null,
        executive_challenge: form.executive_challenge || null,
        approach: form.approach || null,
        strategic_intervention: form.strategic_intervention || null,
        business_outcomes: form.business_outcomes || null,
        key_takeaways: form.key_takeaways || null,
        cta: form.cta || null,
      };
      let saved;
      if (isNew) { const { data } = await api.post("/admin/blogs", payload); saved = data; }
      else { const { data } = await api.put(`/admin/blogs/${id}`, payload); saved = data; }
      toast.success(nextStatus === "published" ? "Article published" : "Article saved");
      if (isNew) nav(`/adminia/blogs/${saved.id}/edit`, { replace: true });
      else setForm((f) => ({ ...f, status: saved.status, slug: saved.slug }));
    } catch (err) {
      const msg = err?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally { setSaving(false); }
  };

  if (!loaded) return <div className="py-12 text-center font-body text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/adminia" className="inline-flex items-center gap-2 font-body text-sm text-slate-500 hover:text-navy">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="admin-ai-assistant" type="button" onClick={() => { setAiOpen(true); setAiResult(null); }} className="inline-flex items-center gap-2 rounded-full border border-royal/30 bg-royal/5 px-4 py-2.5 font-body text-sm font-semibold text-royal hover:bg-royal/10">
            <Sparkles className="h-4 w-4" /> AI assistant
          </button>
          <button data-testid="admin-save-draft" disabled={saving} onClick={() => save("draft")} className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 font-body text-sm font-semibold text-navy hover:bg-slate-50 disabled:opacity-60">
            <Save className="h-4 w-4" /> Save as draft
          </button>
          <button data-testid="admin-publish" disabled={saving} onClick={() => save("published")} className="inline-flex items-center gap-2 rounded-full bg-royal px-5 py-2.5 font-body text-sm font-semibold text-white hover:bg-navy disabled:opacity-60">
            <UploadCloud className="h-4 w-4" /> {form.status === "published" ? "Update & keep live" : "Publish now"}
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="space-y-4 lg:col-span-8">
          <input data-testid="admin-title" required value={form.title} onChange={set("title")} placeholder="Article title" className="w-full rounded-[1rem] border border-slate-200 bg-white px-5 py-4 font-heading text-2xl font-bold tracking-tight text-navy outline-none focus:border-royal md:text-3xl" />
          <input data-testid="admin-subtitle" value={form.subtitle} onChange={set("subtitle")} placeholder="Subtitle (optional)" className="w-full rounded-[1rem] border border-slate-200 bg-white px-5 py-3 font-body text-lg text-slate-700 outline-none focus:border-royal" />
          <textarea data-testid="admin-excerpt" value={form.excerpt} onChange={set("excerpt")} placeholder="Short excerpt (shown on the list)" rows={2} className="w-full rounded-[1rem] border border-slate-200 bg-white px-5 py-4 font-body text-base text-slate-700 outline-none focus:border-royal" />

          <div className="overflow-hidden rounded-[1rem] border border-slate-200 bg-white">
            {editor && (
              <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-3 py-2">
                <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}><Bold className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}><Italic className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}><Heading2 className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}><Heading3 className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}><List className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}><ListOrdered className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")}><Quote className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => { const url = window.prompt("URL"); if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run(); }}><LinkIcon className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => inlineInput.current?.click()} title="Insert image"><ImgIcon className="h-4 w-4" /></ToolbarBtn>
                <input ref={inlineInput} type="file" accept="image/*" onChange={onInline} className="hidden" />
                <div className="mx-1 h-5 w-px bg-slate-300" />
                <ToolbarBtn onClick={() => editor.chain().focus().undo().run()}><Undo className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().redo().run()}><Redo className="h-4 w-4" /></ToolbarBtn>
              </div>
            )}
            <EditorContent editor={editor} />
          </div>
        </div>

        <aside className="space-y-4 lg:col-span-4">
          <Panel title="Publishing destination">
            <div className="space-y-2">
              {[
                { v: "insights", t: "Website Insights", d: "Public, evergreen, SEO-indexed content" },
                { v: "success-stories", t: "Success Stories", d: "Public executive success stories" },
              ].map((o) => (
                <label key={o.v} data-testid={`dest-${o.v}`} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${form.destination === o.v ? "border-royal bg-royal/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <input type="radio" name="destination" value={o.v} checked={form.destination === o.v} onChange={() => setForm((f) => ({ ...f, destination: o.v }))} className="mt-1 accent-royal" />
                  <div>
                    <p className="font-heading text-sm font-bold text-navy">{o.t}</p>
                    <p className="mt-0.5 font-body text-xs text-slate-500">{o.d}</p>
                  </div>
                </label>
              ))}
            </div>
          </Panel>

          {form.destination === "success-stories" && (
            <Panel title="Success story detail">
              <Field label="Industry"><input data-testid="ss-industry" value={form.industry} onChange={set("industry")} placeholder="e.g. Manufacturing" className={inp} /></Field>
              <Field label="Business situation"><textarea data-testid="ss-situation" rows={3} value={form.business_situation} onChange={set("business_situation")} placeholder="The context the business was in" className={inp} /></Field>
              <Field label="Executive challenge"><textarea data-testid="ss-challenge" rows={3} value={form.executive_challenge} onChange={set("executive_challenge")} placeholder="The question on the board's agenda" className={inp} /></Field>
              <Field label="Approach"><textarea data-testid="ss-approach" rows={3} value={form.approach} onChange={set("approach")} placeholder="How the engagement was framed" className={inp} /></Field>
              <Field label="Strategic intervention"><textarea data-testid="ss-intervention" rows={3} value={form.strategic_intervention} onChange={set("strategic_intervention")} placeholder="What was done" className={inp} /></Field>
              <Field label="Business outcomes"><textarea data-testid="ss-outcomes" rows={3} value={form.business_outcomes} onChange={set("business_outcomes")} placeholder="Result the business realised" className={inp} /></Field>
              <Field label="Key takeaways"><textarea data-testid="ss-takeaways" rows={3} value={form.key_takeaways} onChange={set("key_takeaways")} placeholder="Lessons any CEO can apply" className={inp} /></Field>
              <Field label="Call to action"><input data-testid="ss-cta" value={form.cta} onChange={set("cta")} placeholder="e.g. Book a strategy session" className={inp} /></Field>
            </Panel>
          )}

          <Panel title="Featured image">
            {form.hero_image ? (
              <div className="space-y-2">
                <img src={resolveImageUrl(form.hero_image)} alt="Hero" className="h-40 w-full rounded-lg object-cover" />
                <div className="flex gap-2">
                  <button onClick={() => heroInput.current?.click()} className="flex-1 rounded-full border border-slate-300 bg-white py-2 font-body text-xs font-semibold hover:bg-slate-50">Replace</button>
                  <button onClick={() => setForm((f) => ({ ...f, hero_image: null }))} className="flex-1 rounded-full border border-slate-300 bg-white py-2 font-body text-xs font-semibold text-rose-600 hover:bg-rose-50">Remove</button>
                </div>
              </div>
            ) : (
              <button onClick={() => heroInput.current?.click()} className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-8 font-body text-sm text-slate-500 hover:border-royal hover:text-royal">
                <UploadCloud className="h-6 w-6" />
                {uploading ? "Uploading…" : "Click to upload"}
              </button>
            )}
            <input ref={heroInput} type="file" accept="image/*" onChange={onHero} className="hidden" />
          </Panel>

          <Panel title="Metadata">
            <Field label="Slug"><input value={form.slug} onChange={set("slug")} placeholder="auto from title" className={inp} /></Field>
            <Field label="Category"><input value={form.category} onChange={set("category")} className={inp} /></Field>
            <Field label="Tags (comma separated)"><input value={form.tags} onChange={set("tags")} className={inp} /></Field>
            <Field label="Author"><input value={form.author} onChange={set("author")} className={inp} /></Field>
          </Panel>

          <Panel title="SEO">
            <Field label="Meta title"><input value={form.meta_title} onChange={set("meta_title")} placeholder="Defaults to title" className={inp} /></Field>
            <Field label="Meta description"><textarea rows={3} value={form.meta_description} onChange={set("meta_description")} placeholder="Defaults to excerpt" className={inp} /></Field>
          </Panel>

          <Panel title="Status">
            <span className={`inline-flex items-center rounded-full px-3 py-1 font-body text-xs font-semibold ${form.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{form.status}</span>
          </Panel>
        </aside>
      </div>

      {aiOpen && (
        <div data-testid="ai-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 backdrop-blur-sm p-4" onClick={() => setAiOpen(false)}>
          <div className="w-full max-w-3xl rounded-[1.25rem] border border-slate-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-royal" />
                <div>
                  <h3 className="font-heading text-lg font-bold text-navy">AI content assistant</h3>
                  <p className="font-body text-xs text-slate-500">Claude Sonnet 5, executive editorial tone</p>
                </div>
              </div>
              <button onClick={() => setAiOpen(false)} className="rounded-full p-2 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6">
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "seo", t: "SEO pack" },
                  { k: "linkedin", t: "LinkedIn post" },
                  { k: "twitter", t: "X post" },
                  { k: "newsletter", t: "Newsletter" },
                ].map((o) => (
                  <button
                    key={o.k}
                    data-testid={`ai-kind-${o.k}`}
                    disabled={aiBusy}
                    onClick={() => generateAI(o.k)}
                    className={`rounded-full px-4 py-2 font-body text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${aiKind === o.k ? "bg-royal text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"} disabled:opacity-60`}
                  >
                    {o.t}
                  </button>
                ))}
              </div>
              <div className="mt-5 min-h-[240px] rounded-lg border border-slate-200 bg-slate-50 p-5">
                {aiBusy ? (
                  <p className="font-body text-sm text-slate-500">Drafting…</p>
                ) : !aiResult ? (
                  <p className="font-body text-sm text-slate-500">Pick a format above to generate copy from the current article.</p>
                ) : aiResult.raw ? (
                  <div>
                    <pre className="whitespace-pre-wrap font-body text-sm text-slate-700">{aiResult.raw}</pre>
                    <button onClick={() => copyText(aiResult.raw)} className="mt-3 inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 font-body text-xs font-semibold hover:border-royal hover:text-royal"><Copy className="h-3 w-3" /> Copy</button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(aiResult).map(([k, v]) => {
                      const isHtml = k === "body_html";
                      return (
                        <div key={k}>
                          <div className="flex items-center justify-between">
                            <p className="font-body text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">{k.replace(/_/g, " ")}</p>
                            <div className="flex items-center gap-2">
                              {aiKind === "seo" && k === "meta_title" && (
                                <button onClick={() => applyAI("meta_title", v)} className="rounded-md border border-slate-300 bg-white px-2 py-1 font-body text-[11px] font-semibold hover:border-royal hover:text-royal"><Clipboard className="mr-1 inline h-3 w-3" />Apply</button>
                              )}
                              {aiKind === "seo" && k === "meta_description" && (
                                <button onClick={() => applyAI("meta_description", v)} className="rounded-md border border-slate-300 bg-white px-2 py-1 font-body text-[11px] font-semibold hover:border-royal hover:text-royal"><Clipboard className="mr-1 inline h-3 w-3" />Apply</button>
                              )}
                              {aiKind === "seo" && k === "excerpt" && (
                                <button onClick={() => applyAI("excerpt", v)} className="rounded-md border border-slate-300 bg-white px-2 py-1 font-body text-[11px] font-semibold hover:border-royal hover:text-royal"><Clipboard className="mr-1 inline h-3 w-3" />Apply</button>
                              )}
                              <button onClick={() => copyText(typeof v === "string" ? v : JSON.stringify(v, null, 2))} className="rounded-md border border-slate-300 bg-white px-2 py-1 font-body text-[11px] font-semibold hover:border-royal hover:text-royal"><Copy className="mr-1 inline h-3 w-3" />Copy</button>
                            </div>
                          </div>
                          {isHtml ? (
                            <div className="mt-1 rounded-md border border-slate-200 bg-white p-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: v }} />
                          ) : (
                            <p className="mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-white p-3 font-body text-sm text-slate-700">{v}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full rounded-md border border-slate-200 bg-white px-3 py-2 font-body text-sm outline-none focus:border-royal";
function Panel({ title, children }) { return <div className="rounded-[1rem] border border-slate-200 bg-white p-5"><p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{title}</p><div className="mt-3 space-y-3">{children}</div></div>; }
function Field({ label, children }) { return <div><label className="font-body text-xs font-semibold text-slate-600">{label}</label><div className="mt-1">{children}</div></div>; }
function ToolbarBtn({ onClick, active, children, title }) { return <button type="button" onClick={onClick} title={title} className={`inline-flex h-8 w-8 items-center justify-center rounded-md ${active ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-200"}`}>{children}</button>; }
